import api from '../api/axios';

/** A point on the daily cost/call time-series. */
export interface AiUsageDailyPoint {
  date: string; // YYYY-MM-DD
  calls: number;
  cost: number;
}

export interface AiUsageFeatureRow {
  feature: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}

export interface AiUsageModelRow {
  modelId: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  unpricedCalls: number;
}

export interface AiUsageUserRow {
  userId: number | null;
  userName: string;
  calls: number;
  cost: number;
}

export interface AiUsageChildRow {
  childId: number | null;
  childName: string;
  calls: number;
  cost: number;
}

export interface AiUsageSummary {
  range: { from: string; to: string };
  currency: string;
  currencies: string[];
  totals: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cost: number;
    unpricedCalls: number;
    failedCalls: number;
  };
  byFeature: AiUsageFeatureRow[];
  byModel: AiUsageModelRow[];
  byUser: AiUsageUserRow[];
  byChild: AiUsageChildRow[];
  daily: AiUsageDailyPoint[];
}

/** A grouped aggregate row from /events?groupBy=… */
export interface AiUsageGroupRow {
  key: string;
  label: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  unpricedCalls: number;
}

export type AiUsageGroupBy = 'feature' | 'model' | 'user' | 'child' | 'post';

export interface AiUsageGroupedResponse {
  range: { from: string; to: string };
  groupBy: AiUsageGroupBy;
  groups: AiUsageGroupRow[];
}

/** A single granular AI call. */
export interface AiUsageEvent {
  id: number;
  occurredAt: string; // ISO timestamp
  feature: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number | null;
  succeeded: boolean;
  postId: number | null;
  postSnippet: string | null;
  childId: number | null;
  childName: string | null;
  userId: number | null;
  userName: string | null;
  deviceUserId: number | null;
  deviceUserName: string | null;
  priced: boolean;
  cost: number | null;
  currency: string | null;
}

export interface AiUsageEventsResponse {
  range: { from: string; to: string };
  limit: number;
  offset: number;
  events: AiUsageEvent[];
}

export interface AiModelPrice {
  modelId: string;
  inputPer1k: number;
  outputPer1k: number;
  currency: string;
  effectiveFrom: string; // YYYY-MM-DD
}

/**
 * AI-usage reporting for the System Admin area (SYSTEM_ADMIN_EMAILS-gated on
 * the backend; org admins have no access — this is platform cost telemetry).
 * The caller names the organisation to inspect. Backed by
 * ai.v_usage_event_costed which derives cost from the dated ai.model_price book.
 */
export class AiUsageDataService {
  private static base(orgId: number): string {
    return `/system-admin/organisations/${orgId}/ai-usage`;
  }

  /** Period totals + breakdowns (feature/model/user/child) + daily series. */
  static async fetchSummary(orgId: number, from?: string, to?: string): Promise<AiUsageSummary> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await api.get(`${this.base(orgId)}/summary`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  }

  /** Grouped aggregates for the "Group by" breakdown table. */
  static async fetchGrouped(
    orgId: number,
    groupBy: AiUsageGroupBy,
    from?: string,
    to?: string,
    opts?: { feature?: string; userId?: number },
  ): Promise<AiUsageGroupedResponse> {
    const params: Record<string, string> = { groupBy };
    if (from) params.from = from;
    if (to) params.to = to;
    if (opts?.feature) params.feature = opts.feature;
    if (opts?.userId != null) params.userId = String(opts.userId);
    const response = await api.get(`${this.base(orgId)}/events`, { params });
    return response.data;
  }

  /** Granular per-call rows for the drill-down table. */
  static async fetchEvents(
    orgId: number,
    from?: string,
    to?: string,
    opts?: { feature?: string; userId?: number; limit?: number; offset?: number },
  ): Promise<AiUsageEventsResponse> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (opts?.feature) params.feature = opts.feature;
    if (opts?.userId != null) params.userId = String(opts.userId);
    if (opts?.limit != null) params.limit = String(opts.limit);
    if (opts?.offset != null) params.offset = String(opts.offset);
    const response = await api.get(`${this.base(orgId)}/events`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  }

  /** Read-only model price book (global — the org in the path is incidental). */
  static async fetchPrices(orgId: number): Promise<AiModelPrice[]> {
    const response = await api.get(`${this.base(orgId)}/prices`);
    return response.data.prices || [];
  }
}
